import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, RotateCw } from "lucide-react";
import { formatCurrency } from "@/components/StatusBadge";

/**
 * Admin-only multi-line item editor.
 *
 * Props:
 *   invoice: the invoice being edited (must include id, currency, entity_type, line_items?, invoice_amount, tax_amount, line_description, hsn_sac_code, rate, quantity)
 *   open, onOpenChange: dialog control
 *   onSaved: callback with the updated invoice
 */
export default function LineItemsEditor({ invoice, open, onOpenChange, onSaved }) {
  const [lines, setLines] = useState([]);
  const [manualTotal, setManualTotal] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [saving, setSaving] = useState(false);

  const isIndia = invoice?.entity_type === "india" || invoice?.currency === "INR";
  const currency = invoice?.currency || "USD";

  useEffect(() => {
    if (!invoice) return;
    // Seed the editor: use existing line_items if present, else convert legacy single line
    let seed = invoice.line_items && invoice.line_items.length ? invoice.line_items : null;
    if (!seed) {
      seed = [{
        description: invoice.line_description || "Professional services",
        hsn_sac_code: invoice.hsn_sac_code || (isIndia ? "998323" : ""),
        quantity: Number(invoice.quantity || 1),
        rate: Number(invoice.rate || invoice.invoice_amount || 0),
        amount: Number(invoice.invoice_amount || 0),
      }];
    }
    setLines(seed.map((l) => ({
      _key: crypto.randomUUID?.() || `l-${Date.now()}-${Math.random()}`,
      description: l.description || "",
      hsn_sac_code: l.hsn_sac_code || "",
      quantity: Number(l.quantity ?? 1),
      rate: Number(l.rate ?? 0),
      amount: l.amount != null ? Number(l.amount) : Number(l.quantity ?? 1) * Number(l.rate ?? 0),
    })));
    setInvoiceAmount(Number(invoice.invoice_amount || 0));
    setTaxAmount(Number(invoice.tax_amount || 0));
    setManualTotal(!!invoice.manual_total);
  }, [invoice, isIndia]);

  const subTotal = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const displayedAmount = manualTotal ? invoiceAmount : subTotal;

  const recomputeAmount = (idx, partial) => {
    setLines((cur) => cur.map((l, i) => {
      if (i !== idx) return l;
      const merged = { ...l, ...partial };
      // Recompute amount from qty*rate unless the user directly typed into `amount`
      if (!("amount" in partial)) {
        merged.amount = Number((Number(merged.quantity || 0) * Number(merged.rate || 0)).toFixed(2));
      }
      return merged;
    }));
  };

  const addLine = () => setLines((cur) => [
    ...cur,
    { _key: crypto.randomUUID?.() || `l-${Date.now()}-${Math.random()}`, description: "", hsn_sac_code: isIndia ? "998323" : "", quantity: 1, rate: 0, amount: 0 },
  ]);
  const removeLine = (idx) => setLines((cur) => cur.filter((_, i) => i !== idx));

  const save = async () => {
    if (!lines.length) return toast.error("At least one line item is required");
    if (lines.some((l) => !l.description.trim())) return toast.error("Every line needs a description");
    setSaving(true);
    try {
      const payload = {
        line_items: lines.map((l) => ({
          description: l.description.trim(),
          hsn_sac_code: l.hsn_sac_code || null,
          quantity: Number(l.quantity || 0),
          rate: Number(l.rate || 0),
          amount: Number(l.amount || 0),
        })),
        manual_total: manualTotal,
        tax_amount: Number(taxAmount || 0),
      };
      if (manualTotal) payload.invoice_amount = Number(invoiceAmount || 0);
      const { data } = await api.patch(`/api/invoices/${invoice.id}`, payload);
      toast.success("Line items saved");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save line items");
    } finally {
      setSaving(false);
    }
  };

  const resetToLegacy = () => {
    // Blow away multi-line and put back the single-line fields
    setLines([{
      _key: crypto.randomUUID?.() || `l-${Date.now()}-${Math.random()}`,
      description: invoice.line_description || "Professional services",
      hsn_sac_code: invoice.hsn_sac_code || (isIndia ? "998323" : ""),
      quantity: Number(invoice.quantity || 1),
      rate: Number(invoice.rate || invoice.invoice_amount || 0),
      amount: Number(invoice.invoice_amount || 0),
    }]);
    setManualTotal(false);
    setInvoiceAmount(Number(invoice.invoice_amount || 0));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="line-items-editor">
        <DialogHeader>
          <DialogTitle>Line Items — Invoice {invoice?.invoice_number}</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-500 -mt-2 mb-2">
          Admin-only editor. Prints on the invoice PDF as a multi-row line item table.
          By default the invoice total is auto-synced from the sum of line items.
        </div>

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="px-3 py-2 w-8">#</th>
                <th className="px-3 py-2">Description</th>
                {isIndia && <th className="px-3 py-2 w-28">HSN/SAC</th>}
                <th className="px-3 py-2 w-24 text-right">Qty</th>
                <th className="px-3 py-2 w-32 text-right">Rate</th>
                <th className="px-3 py-2 w-32 text-right">Amount</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l._key || `line-${idx}`} className="border-t border-slate-100" data-testid={`line-item-row-${idx}`}>
                  <td className="px-3 py-1.5 text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-1.5">
                    <Input
                      value={l.description}
                      onChange={(e) => recomputeAmount(idx, { description: e.target.value })}
                      placeholder="e.g. Payment Stage 01 — 50% of design fee"
                      data-testid={`line-item-description-${idx}`}
                    />
                  </td>
                  {isIndia && (
                    <td className="px-3 py-1.5">
                      <Input
                        value={l.hsn_sac_code || ""}
                        onChange={(e) => recomputeAmount(idx, { hsn_sac_code: e.target.value })}
                        placeholder="998323"
                        data-testid={`line-item-hsn-${idx}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-1.5">
                    <Input
                      type="number" min="0" step="any"
                      value={l.quantity}
                      onChange={(e) => recomputeAmount(idx, { quantity: e.target.value })}
                      className="text-right"
                      data-testid={`line-item-qty-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <Input
                      type="number" min="0" step="any"
                      value={l.rate}
                      onChange={(e) => recomputeAmount(idx, { rate: e.target.value })}
                      className="text-right"
                      data-testid={`line-item-rate-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <Input
                      type="number" min="0" step="any"
                      value={l.amount}
                      onChange={(e) => recomputeAmount(idx, { amount: e.target.value })}
                      className="text-right font-medium"
                      data-testid={`line-item-amount-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => removeLine(idx)}
                      disabled={lines.length === 1}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`line-item-delete-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td colSpan={isIndia ? 5 : 4} className="px-3 py-2 text-right text-slate-500">Subtotal (sum of line amounts)</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(subTotal, currency)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex justify-between items-center">
          <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add line
          </Button>
          <Button size="sm" variant="ghost" onClick={resetToLegacy} className="text-slate-500" data-testid="reset-line-items-btn">
            <RotateCw className="w-3.5 h-3.5 mr-1" /> Reset to single line
          </Button>
        </div>

        <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4 items-start">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={manualTotal}
                onCheckedChange={(v) => setManualTotal(!!v)}
                data-testid="manual-total-checkbox"
              />
              Override invoice total (keep a manual amount even if line items don't match)
            </label>
            <div className={`text-xs ${manualTotal ? "text-amber-700" : "text-slate-500"}`}>
              {manualTotal
                ? `Invoice total will be locked at ${formatCurrency(invoiceAmount, currency)} regardless of line sums.`
                : `Invoice total will auto-sync to line-item subtotal.`}
            </div>
          </div>
          <div className="space-y-2">
            {manualTotal && (
              <div className="space-y-1.5">
                <Label>Invoice Amount (manual)</Label>
                <Input
                  type="number" step="any"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  data-testid="manual-invoice-amount"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Tax</Label>
              <Input
                type="number" step="any"
                value={taxAmount}
                onChange={(e) => setTaxAmount(e.target.value)}
                data-testid="line-items-tax"
              />
            </div>
            <div className="border-t border-slate-200 pt-2 flex justify-between text-sm">
              <span className="text-slate-500">Effective invoice total</span>
              <span className="font-semibold tabular-nums">
                {formatCurrency(displayedAmount + Number(taxAmount || 0), currency)}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-brand hover:bg-brand-hover"
            onClick={save}
            disabled={saving || lines.length === 0}
            data-testid="save-line-items-btn"
          >
            {saving ? "Saving…" : "Save line items"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
